import { compareAsc, isBefore, subDays } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { parseServerDateTime } from '../../../api/parsers/commons-api-parsers';
import { ApiRequestStatus } from '../../../model/api-request';
import { GlobalActions } from '../../../model/global-actions';
import { prepareChangeContact } from '../../../store/data/user';
import {
  clearInsertPhoneNumberChangePhoneNumberError,
  clearVerificationPhoneNumberChangePhoneNumberError,
  resetChangePhoneNumberState,
  resetInsertPhoneNumberChangePhoneNumberRequestState,
  resetVerificationPhoneNumberChangePhoneNumberRequestState,
  setConfirmNewPhoneNumberChangePhoneNumber,
  setPhoneNumberChangePhoneNumber,
  setVerificationEmailCodeChangePhoneNumberCode,
  setVerificationPhoneNumberCodeChangePhoneNumberCode,
  updateCountdownPhoneNumber,
  updateErrorPhoneNumberNotEditable,
  updateOpenDialogChangePhoneNumber,
  updatePhoneNumberAlreadyVerificated,
  updateStepPhoneNumber,
} from '../../../store/features/profile/change-phone-number/change-phone-number-slice';
import {
  insertPhoneNumberChangePhoneNumber,
  verificationPhoneNumberChangePhoneNumber,
} from '../../../store/features/profile/change-phone-number/change-phone-number-thunk';
import {
  PhoneNumberNotEditableType,
  StepsChangePhoneNumber,
} from '../../../store/features/profile/change-phone-number/change-phone-number-type';
import {
  messagesForChangePhoneNumberExpiredDocumentError,
  messagesForInsertPhoneNumberChangePhoneNumberError,
  messagesForVerificationPhoneNumberChangePhoneNumberError,
} from '../../../store/features/profile/change-phone-number/change-phone-number-utils';
import { initializeClock } from '../../../utils/coundown';
import { useAppSelector } from '../../redux';
import { resetPrepareChangeContactState } from '../../../store/data/user';

export interface ChangePhoneNumberDialogLogicCallbacks {
  onChangePhoneNumber: () => void;

  disabledButtonFirstStep: boolean;
  phoneNumber: string;
  confirmNewPhoneNumber: string;
  insertPhoneNumberChangePhoneNumberRequestStatus: ApiRequestStatus;

  updatePhoneNumber: (value?: string) => void;
  updateConfirmNewPhoneNumberChangePhoneNumber: (value?: string) => void;
  handleSubmitInsertPhoneNumber: () => void;

  changePhoneNumberVerificationCodeEmail: string;
  changePhoneNumberVerificationCodePhoneNumber: string;

  updateVerificationCodeEmail: (value?: string) => void;
  updateVerificationCodePhoneNumber: (value?: string) => void;

  verificationPhoneNumberChangePhoneNumberRequestStatus: ApiRequestStatus;
  prepareChangeContactRequestStatus: ApiRequestStatus;

  handleSubmitVerificationPhoneNumber: () => void;

  step: StepsChangePhoneNumber;
  openDialogChangePhoneNumber: boolean;

  closeChangePhoneNumber: () => void;

  closeStep3ChangePhoneNumber: () => void;
  tooManyAttemptsErrorChangePhoneNumber: boolean;
  handleCloseErrorTooManyAttemptsChangePhoneNumber: () => void;

  backToStep1: () => void;
  backToStep2: () => void;

  errorPhoneNumberNotEditable: PhoneNumberNotEditableType;
  phoneNumberAlreadyVerificated: boolean;
  closeErrorPhoneNumberNotEditable: () => void;
  buttonDisabled: boolean;

  countdown: string;
  timeoutError: boolean;

  changePhoneDisabled: boolean;
}

export const useChangePhoneNumberDialogLogic = (
  actions: GlobalActions
): ChangePhoneNumberDialogLogicCallbacks => {
  const { showToast, closeToast } = actions;

  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const intl = useIntl();

  const {
    phoneNumber,
    confirmNewPhoneNumber,
    insertPhoneNumberChangePhoneNumberRequestStatus,
    insertPhoneNumberChangePhoneNumberError,

    changePhoneNumberVerificationCodeEmail,
    changePhoneNumberVerificationCodePhoneNumber,
    verificationPhoneNumberChangePhoneNumberRequestStatus,
    verificationPhoneNumberChangePhoneNumberError,

    step,
    openDialogChangePhoneNumber,
    errorPhoneNumberNotEditable,
    phoneNumberAlreadyVerificated,
    
    endtimeCountdown,
    countdown,
  }: undefined | any = useAppSelector(
    (state) => state.features.changePhoneNumber
  );

  const {
    signFiles,
    signFilesPhoneNumber,
    phoneUpdatedDate,
    signFilesEmail,
    phone,
    prepareChangeContactRequestStatus,
    prepareChangeContactError,
    changeContactsAvailable,
    documentiScaduti,
  } = useAppSelector((state) => state.data.user);

  const [
    tooManyAttemptsErrorChangePhoneNumber,
    setTooManyAttemptsErrorChangePhoneNumber,
  ] = useState(false);

  const buttonDisabled = useMemo(() => {
    const isEmptyCodeEmail =
      changePhoneNumberVerificationCodeEmail?.length === 0;
    const isEmptyCodePhone =
      changePhoneNumberVerificationCodePhoneNumber?.length === 0;
    return isEmptyCodeEmail || isEmptyCodePhone;
  }, [
    changePhoneNumberVerificationCodeEmail,
    changePhoneNumberVerificationCodePhoneNumber,
  ]);

  const [disabledButtonFirstStep, setDisabledButtonFirstStep] = useState(
    !phoneNumber || phoneNumber.length === 0
  );

  //Open dialog change phone number
  const onChangePhoneNumber = useCallback(() => {
    if (signFilesEmail) {
      dispatch(updateErrorPhoneNumberNotEditable('EMAIL-VERIFICATION'));
    } else if (signFilesPhoneNumber || signFiles) {
      dispatch(updateErrorPhoneNumberNotEditable('OTHER_DOCUMENTS_TO_SIGN'));
    } else if (compareAsc(phoneUpdatedDate, subDays(new Date(), 5)) === 1) {
      dispatch(updateErrorPhoneNumberNotEditable('LAST_UPDATING_UNTIL_5_DAYS'));
    } else {
      dispatch(updateOpenDialogChangePhoneNumber(true));
    }
  }, [
    dispatch,
    phoneUpdatedDate,
    signFiles,
    signFilesEmail,
    signFilesPhoneNumber,
  ]);

  // Change step
  const changeStep = useCallback(
    (newStep: StepsChangePhoneNumber) => {
      dispatch(updateStepPhoneNumber(newStep));
    },
    [dispatch]
  );

  //Reset state, reset steps, anche close change phone number dialog
  const closeChangePhoneNumber = useCallback(() => {
    dispatch(updateOpenDialogChangePhoneNumber(false));
    dispatch(resetChangePhoneNumberState());
  }, [dispatch]);

  // Close step 3 (only for status succeeded)
  const closeStep3ChangePhoneNumber = useCallback(() => {
    dispatch(
      prepareChangeContact({
        contact: phoneNumber,
        oldContact: phone,
        contactType: 'PHONE',
      })
    );
  }, [dispatch, phoneNumber, phone]);

  // Close dialog error too many attempts
  const handleCloseErrorTooManyAttemptsChangePhoneNumber = useCallback(() => {
    setTooManyAttemptsErrorChangePhoneNumber(false);
    onChangePhoneNumber();
  }, [onChangePhoneNumber]);

  // Save phone number
  const updatePhoneNumber = useCallback(
    (value: string) => {
      setDisabledButtonFirstStep(false);
      dispatch(setPhoneNumberChangePhoneNumber(value));
    },
    [dispatch]
  );
  // Save new phone number
  const updateConfirmNewPhoneNumberChangePhoneNumber = useCallback(
    (value: string) => {
      setDisabledButtonFirstStep(false);
      dispatch(setConfirmNewPhoneNumberChangePhoneNumber(value));
    },
    [dispatch]
  );

  //Save verification code
  const updateVerificationCodeEmail = useCallback(
    (value: string) => {
      dispatch(setVerificationEmailCodeChangePhoneNumberCode(value));
    },
    [buttonDisabled, dispatch]
  );

  const updateVerificationCodePhoneNumber = useCallback(
    (value: string) => {
      dispatch(setVerificationPhoneNumberCodeChangePhoneNumberCode(value));
    },
    [buttonDisabled, dispatch]
  );

  //Submit insert phone number
  const handleSubmitInsertPhoneNumber = useCallback(() => {
    dispatch(insertPhoneNumberChangePhoneNumber());
  }, [dispatch]);
  // Submit verification code
  const handleSubmitVerificationPhoneNumber = useCallback(() => {
    dispatch(verificationPhoneNumberChangePhoneNumber());
  }, [dispatch]);

  const backToStep1 = useCallback(() => {
    changeStep('INSERIMENTO_DATI');
  }, [changeStep]);

  const backToStep2 = useCallback(() => {
    changeStep('VERIFICA_OTP');
  }, [changeStep]);

  const closeErrorPhoneNumberNotEditable = useCallback(() => {
    dispatch(updateErrorPhoneNumberNotEditable(undefined));
  }, [dispatch]);

  const emailAttempts = useAppSelector(
    (state) => state.features.changePhoneNumber.emailAttempts
  );
  const phoneNumberAttempts = useAppSelector(
    (state) => state.features.changePhoneNumber.phoneNumberAttempts
  );

  useEffect(() => {
    if (insertPhoneNumberChangePhoneNumberRequestStatus === 'succeeded') {
      changeStep('VERIFICA_OTP');
      dispatch(resetInsertPhoneNumberChangePhoneNumberRequestState());
    }
  }, [changeStep, dispatch, insertPhoneNumberChangePhoneNumberRequestStatus]);

  useEffect(() => {
    if (insertPhoneNumberChangePhoneNumberError) {
      const errorMessages = messagesForInsertPhoneNumberChangePhoneNumberError(
        insertPhoneNumberChangePhoneNumberError
      );
      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title),
        description:
          errorMessages.description && formatMessage(errorMessages.description),
        close: closeToast,
      });
      dispatch(clearInsertPhoneNumberChangePhoneNumberError());
    }
  }, [
    closeToast,
    dispatch,
    formatMessage,
    insertPhoneNumberChangePhoneNumberError,
    showToast,
  ]);

  useEffect(() => {
    if (verificationPhoneNumberChangePhoneNumberRequestStatus === 'succeeded') {
      changeStep('RIEPILOGO');
      dispatch(resetVerificationPhoneNumberChangePhoneNumberRequestState());
      dispatch(updatePhoneNumberAlreadyVerificated(true));
    }
  }, [
    changeStep,
    dispatch,
    verificationPhoneNumberChangePhoneNumberRequestStatus,
  ]);

  useEffect(() => {
    if (prepareChangeContactRequestStatus === 'succeeded') {
      closeChangePhoneNumber();
    } else if (prepareChangeContactRequestStatus === 'failed') {
      const errorMessages = messagesForChangePhoneNumberExpiredDocumentError(
        prepareChangeContactError
      );
      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title),
        description:
          errorMessages.description && formatMessage(errorMessages.description),
        close: closeToast,
      });
      setTimeout(() => {
        dispatch(resetPrepareChangeContactState());
        closeChangePhoneNumber();
      }, 3000);
    }
  }, [prepareChangeContactRequestStatus, prepareChangeContactError]);

  useEffect(() => {
    if (verificationPhoneNumberChangePhoneNumberError) {
      if (
        emailAttempts !== 0 &&
        phoneNumberAttempts !== 0 &&
        (verificationPhoneNumberChangePhoneNumberError?.type ===
          'GENERIC_ERROR' ||
          verificationPhoneNumberChangePhoneNumberError?.type ===
            'CODICE_DI_VERIFICA_SBAGLIATO' ||
          verificationPhoneNumberChangePhoneNumberError?.type ===
            'CODICE_DI_VERIFICA_EMAIL_SBAGLIATO' ||
          verificationPhoneNumberChangePhoneNumberError?.type ===
            'CODICE_DI_VERIFICA_CELLULARE_SBAGLIATO')
      ) {
        const errorMessages =
          messagesForVerificationPhoneNumberChangePhoneNumberError(
            verificationPhoneNumberChangePhoneNumberError,
            intl
          );
        showToast({
          type: 'error',
          title: errorMessages.title,
          description: errorMessages.description,
          close: closeToast,
        });
        dispatch(clearVerificationPhoneNumberChangePhoneNumberError());
      } else if (
        phoneNumberAttempts === 0 ||
        verificationPhoneNumberChangePhoneNumberError?.type ===
          'TROPPI_TENTATIVI'
      ) {
        closeChangePhoneNumber();
        setTooManyAttemptsErrorChangePhoneNumber(true);
      }
      dispatch(clearVerificationPhoneNumberChangePhoneNumberError());
    }
  }, [
    intl,
    verificationPhoneNumberChangePhoneNumberError,
    dispatch,
    formatMessage,
    showToast,
    closeToast,
    closeChangePhoneNumber,
    emailAttempts,
    phoneNumberAttempts,
  ]);

  const [timeoutError, setTimeoutError] = useState(false);

  const setCountdown = useCallback(
    (time: string) => {
      if (openDialogChangePhoneNumber && !time.match('NaN') && !timeoutError) {
        dispatch(updateCountdownPhoneNumber(time));
      }
    },
    [openDialogChangePhoneNumber, timeoutError, dispatch]
  );

  const onTimeChanged = useCallback(
    (minutes: number, seconds: number) => {
      setCountdown(
        `${minutes < 10 ? '0' + minutes : minutes}:${
          seconds < 10 ? '0' + seconds : seconds
        } min`
      );
    },
    [setCountdown]
  );
  useEffect(() => {
    const now = new Date();
    if (isBefore(parseServerDateTime(endtimeCountdown), now)) {
      setTimeoutError(true);
    } else {
      setTimeoutError(false);
      initializeClock(endtimeCountdown, onTimeChanged);
    }
  }, [endtimeCountdown, onTimeChanged]);

  useEffect(() => {
    if (countdown.indexOf('00:00') === 0) {
      setTimeoutError(true);
    }
  }, [countdown]);

  const changePhoneDisabled = useMemo(() => {
    return Boolean(documentiScaduti || !changeContactsAvailable);
  }, [changeContactsAvailable, documentiScaduti]);

  return {
    disabledButtonFirstStep,
    phoneNumber,
    updatePhoneNumber,
    confirmNewPhoneNumber,
    updateConfirmNewPhoneNumberChangePhoneNumber,
    insertPhoneNumberChangePhoneNumberRequestStatus,
    handleSubmitInsertPhoneNumber,

    changePhoneNumberVerificationCodeEmail,
    changePhoneNumberVerificationCodePhoneNumber,
    updateVerificationCodeEmail,
    updateVerificationCodePhoneNumber,
    verificationPhoneNumberChangePhoneNumberRequestStatus,
    handleSubmitVerificationPhoneNumber,

    step,
    openDialogChangePhoneNumber,

    tooManyAttemptsErrorChangePhoneNumber,
    handleCloseErrorTooManyAttemptsChangePhoneNumber,
    closeStep3ChangePhoneNumber,

    closeChangePhoneNumber,
    onChangePhoneNumber,
    prepareChangeContactRequestStatus,
    backToStep1,
    backToStep2,

    errorPhoneNumberNotEditable,
    phoneNumberAlreadyVerificated,
    closeErrorPhoneNumberNotEditable,
    buttonDisabled,
    countdown,
    timeoutError,

    changePhoneDisabled,
  };
};
